import React from "react";
import Text from "@src/lib/ui/Text";
import { Button } from "@src/lib/ui/Button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@src/lib/ui/Tooltip";
import CircleExclamationFillIcon from "@src/lib/icons/18px/circle-exclamation-fill";
import XIcon from "@src/lib/icons/12px/x";
import Spinner from "@src/lib/ui/Spinner";
import { cn } from "@src/lib/utils/cn";
import { libraryItemTypeLabel } from "./libraryItemTypeLabel";
import {
  OpusAgentAttachmentPhase,
  TrainingResourceVersionType,
} from "@src/types.generated";
import type {
  OpusAgentAttachmentLibraryItemFragment,
  OpusAgentAttachmentMediaFragment,
  OpusAgentAttachmentStatusFieldsFragment,
} from "./useOpusAgentAttachments.generated";
import {
  readingImagesCopy,
  readyCopy,
  workingCopy,
} from "./attachmentProgressCopy";
import type { OpusAgentAttachmentPillState } from "./sendGate";
import { OpusAgentAttachmentThumbnail } from "./OpusAgentAttachmentThumbnail";

// Duplicated (and trimmed) from aiAssist's ChatAttachment pill: the outline
// chat is going away. A sent message's pills omit `state` (always ready).
export interface OpusAgentAttachmentPillProps {
  state?: OpusAgentAttachmentPillState;
  detail?: string | null;
  fileName?: string;
  media?: OpusAgentAttachmentMediaFragment | null;
  libraryItem?: OpusAgentAttachmentLibraryItemFragment | null;
  onRemove?: () => void;
  // A failed pill that can be tried again (see canRetryAttachment).
  onRetry?: () => void;
  // The composer's progress: upload percent, then the server's step.
  uploadPercent?: number | null;
  status?: OpusAgentAttachmentStatusFieldsFragment | null;
  className?: string;
  // Stretch to the container (the composer's tray) instead of 150–200px.
  fill?: boolean;
  "data-testid"?: string;
}

// A video's URL becomes its Mux stream playlist once uploaded; that name
// (`<asset>.m3u8`) means nothing to the author.
const STREAM_PLAYLIST = /\.m3u8$/i;

// Uploads are stored as `<name>-<epoch ms>.<ext>` (useS3Upload); the author
// knows the file as `<name>.<ext>`.
const UPLOAD_TIMESTAMP = /-\d{13}(?=\.[^.]+$)/;

const fileNameFromUrl = (url: string): string | null => {
  const last = (url.split("?")[0] ?? "").split("/").pop() || "";
  if (STREAM_PLAYLIST.test(last)) return null;
  let decoded = last;
  try {
    decoded = decodeURIComponent(last);
  } catch {
    // Keep the raw segment.
  }
  return decoded.replace(UPLOAD_TIMESTAMP, "");
};

const typeLabelFromUrl = (url: string): string =>
  STREAM_PLAYLIST.test(url.split("?")[0] ?? "")
    ? "Video"
    : ((url.split("?")[0] ?? "").split(".").pop() || "").toUpperCase();

const FAILED_COPY = "Couldn't read file";

export const OpusAgentAttachmentPill: React.FC<
  OpusAgentAttachmentPillProps
> = ({
  state = "ready",
  detail,
  fileName,
  media,
  libraryItem,
  onRemove,
  onRetry,
  uploadPercent,
  status,
  className,
  fill = false,
  "data-testid": testId,
}) => {
  // The name the author picked the file under wins while it is known (the
  // composer); a sent turn only has the stored URL.
  const name =
    libraryItem?.name.en ||
    fileName ||
    (media ? fileNameFromUrl(media.mediaUrls.en) : null) ||
    (media && STREAM_PLAYLIST.test(media.mediaUrls.en) ? "Video" : null) ||
    "Attachment";

  let typeLabel = "";
  // The file the server reads: the upload, or a file resource's draft file.
  let fileUrl = media?.mediaUrls.en ?? "";
  if (libraryItem) {
    const version =
      libraryItem.trainingResource?.draftVersion ??
      libraryItem.trainingResource?.publishedVersion;
    if (version?.type === TrainingResourceVersionType.Media) {
      fileUrl = version.media?.mediaUrls?.en || "";
      typeLabel = `${typeLabelFromUrl(fileUrl)} Resource`;
    } else if (version?.type === TrainingResourceVersionType.RichTextDocument) {
      typeLabel = "Opus Doc";
    } else {
      typeLabel = libraryItemTypeLabel(libraryItem.type);
    }
  } else if (media) {
    typeLabel = typeLabelFromUrl(media.mediaUrls.en);
  }
  const isBusy = state === "uploading" || state === "processing";
  const failed = state === "failed";
  // A retryable failure says so briefly, with why on hover. One refused
  // before upload ("Over 50 MB") says why in the pill: retrying can't help.
  // A ready upload says what the read found; a sent turn's pill has no
  // status and keeps its type label.
  let subtitle = typeLabel;
  if (failed) subtitle = (!onRetry && detail) || FAILED_COPY;
  else if (isBusy)
    subtitle = workingCopy(
      state,
      uploadPercent,
      status,
      fileName ?? fileNameFromUrl(fileUrl) ?? undefined,
    );
  else if (status?.phase === OpusAgentAttachmentPhase.ReadingImages)
    // Sendable while its pictures finish: an upload or a file-backed item.
    subtitle = readingImagesCopy(status);
  else if (status && fileName && !libraryItem)
    subtitle = readyCopy(fileName, status) ?? typeLabel;
  // Changes with the step, not with each upload percent, so each new step
  // fades in once.
  const stepKey = `${state}:${status?.phase ?? ""}`;
  // The pill list is a live region: while uploading, screen readers hear
  // the step, not each percent.
  const uploading = state === "uploading";
  const tooltip = detail || name;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            "relative flex min-w-0",
            fill ? "w-full" : "w-50 max-w-50 min-w-37.5",
          )}
          data-testid={testId}
          data-state={state}
        >
          <div
            className={cn(
              "flex min-w-0 flex-1 cursor-default items-center gap-2 overflow-hidden rounded-lg bg-bg-secondary p-1",
              // In the composer's tray: one 32px line, name then status.
              fill ? "h-8" : "h-10",
              onRemove ? "pr-1" : "pr-3",
              failed && "bg-status-error-bg",
              className,
            )}
          >
            <div
              className={cn(
                "flex shrink-0 items-center justify-center",
                fill ? "size-6" : "size-8",
              )}
            >
              {isBusy ? (
                <Spinner size={fill ? 14 : 16} label="" />
              ) : failed ? (
                <CircleExclamationFillIcon
                  aria-hidden
                  className="size-4 text-status-error-text"
                />
              ) : (
                <OpusAgentAttachmentThumbnail
                  libraryItem={libraryItem}
                  media={media}
                  fileName={fileName}
                  size={fill ? 24 : 32}
                  // Nested in the card's corner: 6 − 4 → rounded (the floor).
                  className={fill ? "rounded" : "rounded-md"}
                />
              )}
            </div>
            <div
              className={cn(
                "flex min-w-0 flex-1 self-stretch",
                fill ? "items-center gap-2" : "flex-col justify-center",
              )}
            >
              {fill ? (
                // One line; the tooltip carries the full name and any reason.
                <Text
                  variant="fine-print-title"
                  className="min-w-0 flex-1 truncate"
                >
                  {name}
                </Text>
              ) : (
                <Text
                  variant="fine-print-title"
                  ellipsisParentFlexDirection="col"
                  className="min-w-24 flex-1"
                  ellipsis
                >
                  {name}
                </Text>
              )}
              <div
                className={cn(
                  "flex min-w-0 items-center gap-1",
                  fill ? "max-w-1/2 shrink-0" : "flex-1",
                )}
              >
                {fill ? (
                  <Text
                    key={stepKey}
                    variant="fine-print-description"
                    className={cn(
                      "min-w-0 truncate text-text-tertiary animate-in fade-in-0 duration-300 motion-reduce:animate-none",
                      failed && "text-status-error-text",
                    )}
                    aria-hidden={uploading || undefined}
                    data-testid="opus-agent-attachment-subtitle"
                  >
                    {subtitle}
                  </Text>
                ) : (
                  <Text
                    key={stepKey}
                    variant="fine-print-description"
                    ellipsisParentFlexDirection="row"
                    className={cn(
                      "min-w-0 flex-1 text-text-secondary animate-in fade-in-0 duration-300 motion-reduce:animate-none",
                      failed && "text-status-error-text",
                    )}
                    ellipsis
                    aria-hidden={uploading || undefined}
                    data-testid="opus-agent-attachment-subtitle"
                  >
                    {subtitle}
                  </Text>
                )}
                {uploading && (
                  <span className="sr-only">
                    {workingCopy(state, null, status)}
                  </span>
                )}
                {/* The reason is otherwise only on hover. */}
                {failed && onRetry && detail && (
                  <span className="sr-only">{`: ${detail}`}</span>
                )}
                {failed && onRetry && (
                  <button
                    type="button"
                    onClick={onRetry}
                    aria-label={`Retry ${name}`}
                    data-testid="opus-agent-attachment-retry"
                    className="shrink-0 rounded-sm text-2xs-v4 font-medium text-status-error-text underline underline-offset-2 hover:no-underline focus-visible:ring-1 focus-visible:ring-brand-border focus-visible:outline-none"
                  >
                    Retry
                  </button>
                )}
              </div>
            </div>
            {onRemove && (
              <Button
                type="button"
                onClick={onRemove}
                variant="ghost"
                size="icon-xs"
                aria-label={`Remove ${name}`}
                data-testid="opus-agent-attachment-remove"
                className="shrink-0 text-icon-tertiary hover:text-icon-primary"
              >
                <XIcon />
              </Button>
            )}
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
};
